import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import { testimonialsAPI } from '../../../lib/api';

const Testimonials = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setLoading(true);
        const response = await testimonialsAPI.getAll({ featured: true });
        
        // Map the API response to match our component's expected format
        const formattedTestimonials = response.testimonials.map(testimonial => ({
          id: testimonial._id,
          name: testimonial.name,
          role: testimonial.position,
          company: testimonial.company,
          avatar: testimonial.avatar,
          content: testimonial.content,
          rating: testimonial.rating,
          category: testimonial.world,
          project: testimonial.project || testimonial.company
        }));
        
        setTestimonials(formattedTestimonials);
      } catch (err) {
        console.error('Failed to fetch testimonials:', err);
        setError(err.message);
        
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  

  const getCategoryColor = (category) => {
    switch (category) {
      case 'web3': return 'warning';
      case 'community': return 'success';
      case 'software': return 'primary';
      default: return 'primary';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'web3': return 'Zap';
      case 'community': return 'Users';
      case 'software': return 'Code';
      default: return 'Star';
    }
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
            Client{' '}
            <span className="bg-gradient-to-r from-accent to-warning bg-clip-text text-transparent">
              Testimonials
            </span>
          </h2>
          <p className="font-body text-text-secondary text-lg max-w-3xl mx-auto">
            Don't just take my word for it. Here's what clients and collaborators say about working with me across all three worlds.
          </p>
          <div className="mt-4">
            <a 
              href="/submit-testimonial" 
              className="inline-flex items-center text-accent hover:text-accent/80 transition-colors"
            >
              <Icon name="PlusCircle" size={16} className="mr-1" />
              <span>Share your experience</span>
            </a>
          </div>
        </motion.div>
        
        {loading && (
          <div className="flex justify-center items-center py-20">
            <Icon name="Loader" size={40} className="animate-spin text-primary" />
          </div>
        )}
        
        {error && !loading && testimonials.length === 0 && (
          <div className="bg-error/10 border border-error/30 rounded-lg p-6 text-center">
            <Icon name="AlertTriangle" size={32} className="text-error mx-auto mb-4" />
            <p className="text-error">Failed to load testimonials. Please try again later.</p>
          </div>
        )}
        
        {!loading && testimonials.length > 0 && (
          <>
            {/* Main Testimonial Display */}
            <div className="relative max-w-4xl mx-auto mb-12">
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-surface rounded-2xl p-8 md:p-12 shadow-elevation-3 border border-border"
              >
                {/* Category Badge */}
                <div className="flex items-center justify-center mb-6">
                  <div className={`flex items-center space-x-2 px-4 py-2 bg-${getCategoryColor(testimonials[activeTestimonial].category)}/10 rounded-full`}>
                    <Icon 
                      name={getCategoryIcon(testimonials[activeTestimonial].category)} 
                      size={16} 
                      className={`text-${getCategoryColor(testimonials[activeTestimonial].category)}`} 
                    />
                    <span className={`font-caption text-${getCategoryColor(testimonials[activeTestimonial].category)} font-medium text-sm capitalize`}>
                      {testimonials[activeTestimonial].category} world
                    </span>
                  </div>
                </div>

                {/* Quote */}
                <div className="text-center mb-8">
                  <Icon name="Quote" size={48} className="text-primary/20 mx-auto mb-6" />
                  <blockquote className="font-body text-lg md:text-xl text-text-primary leading-relaxed">
                    "{testimonials[activeTestimonial].content}"
                  </blockquote>
                </div>

                {/* Rating */}
                <div className="flex justify-center mb-6">
                  {[...Array(testimonials[activeTestimonial].rating)].map((_, index) => (
                    <Icon key={index} name="Star" size={20} className="text-warning fill-current" />
                  ))}
                </div>

                {/* Author Info */}
                <div className="flex items-center justify-center space-x-4">
                  <Image
                    src={testimonials[activeTestimonial].avatar}
                    alt={testimonials[activeTestimonial].name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div className="text-left">
                    <h4 className="font-body font-semibold text-primary text-lg">
                      {testimonials[activeTestimonial].name}
                    </h4>
                    <p className="font-caption text-text-secondary">
                      {testimonials[activeTestimonial].role}
                    </p>
                    <p className="font-caption text-text-secondary text-sm">
                      {testimonials[activeTestimonial].company}
                    </p>
                  </div>
                </div>

                {/* Project Tag */}
                <div className="text-center mt-4">
                  <span className="font-caption text-text-secondary text-sm">
                    Project: {testimonials[activeTestimonial].project}
                  </span>
                </div>
              </motion.div>
            </div>

            {/* Testimonial Navigation */}
            <div className="flex justify-center space-x-2 mb-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === activeTestimonial
                      ? `bg-${getCategoryColor(testimonials[activeTestimonial].category)}`
                      : 'bg-border hover:bg-text-secondary'
                  }`}
                />
              ))}
            </div>

            {/* Navigation Arrows */}
            <div className="flex justify-center space-x-4 mb-12">
              <button
                onClick={() => setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                className="w-12 h-12 bg-surface border border-border rounded-full flex items-center justify-center hover:bg-primary hover:text-surface hover:border-primary transition-colors duration-200"
              >
                <Icon name="ChevronLeft" size={20} />
              </button>
              <button
                onClick={() => setActiveTestimonial((prev) => (prev + 1) % testimonials.length)}
                className="w-12 h-12 bg-surface border border-border rounded-full flex items-center justify-center hover:bg-primary hover:text-surface hover:border-primary transition-colors duration-200"
              >
                <Icon name="ChevronRight" size={20} />
              </button>
            </div>

            {/* Testimonial Grid Preview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.slice(0, 3).map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-surface border border-border rounded-xl p-6 hover:shadow-elevation-2 transition-all duration-300 cursor-pointer"
                  onClick={() => setActiveTestimonial(index)}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <Image
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <h4 className="font-body font-semibold text-primary">{testimonial.name}</h4>
                      <p className="font-caption text-text-secondary text-sm">{testimonial.role}</p>
                    </div>
                  </div>
                  
                  <div className="flex mb-3">
                    {[...Array(testimonial.rating)].map((_, starIndex) => (
                      <Icon key={starIndex} name="Star" size={14} className="text-warning fill-current" />
                    ))}
                  </div>
                  
                  <p className="font-body text-text-secondary text-sm leading-relaxed line-clamp-3">
                    "{testimonial.content.substring(0, 120)}..."
                  </p>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-center mt-16"
            >
              <p className="font-body text-text-secondary mb-6">
                Ready to work together? Let's create something amazing.
              </p>
              <button onClick={() => window.scrollTo({top: document.getElementById('contact').offsetTop, behavior: 'smooth'})} className="bg-accent text-surface px-8 py-4 rounded-lg font-body font-medium hover:bg-accent/90 transition-all duration-200 transform hover:scale-105 shadow-elevation-2">
                Start a Project
              </button>
            </motion.div>
          </>
        )}
      </div>
    </section>
  );
};

export default Testimonials;